import { AuthenticatedDataRequest } from '@eggplant/data-request'
import {
  Combobox,
  DropdownButton,
  Icons,
  NoOptionsPlaceholder
} from '@eggplant/pattern-library'
import { FiPlus } from 'react-icons/fi'
import { LinkToAddWebhook } from '@eggplant/routing'
import { NOTIFICATION_SERVICE } from '@eggplant/api-hosts'
import type {
  ButtonShape,
  GetWebhooksListResponse,
  Options
} from '@eggplant/types'

interface Props {
  placeholder: string
  value: string
  onChange: (value: string) => void
  onDisabledWebhooksChange: (value: string[]) => void
  selectedWebhookProfile: string[]
  isDisabled?: boolean
}

const getOptions = (data: GetWebhooksListResponse, selectedWebhookProfile: string[]) =>
  data.items
    .filter(item =>
      !!item.uuid && !selectedWebhookProfile.includes(item.uuid)
    )
    .map(item => ({
      label: item.name,
      value: item.uuid
    }))

const SelectWebhookProfile = ({
  placeholder,
  value,
  onChange,
  onDisabledWebhooksChange,
  selectedWebhookProfile = [],
  isDisabled = false
}: Props) => {
  const noDataPlaceholder =
    <NoOptionsPlaceholder
      icon={
        <Icons.Webhook
          aria-hidden
          size='24px'
        />
      }
      linkTo={
        <LinkToAddWebhook>
          <FiPlus
            aria-hidden
            size={16}
          />
          New webhook profile
        </LinkToAddWebhook>
      }
      title='No webhook profile found'
    />

  const renderButton = (options: Options) => {
    const currentOption = options.find(item => item.value === value)

    const label = currentOption?.value
      ? currentOption.label
      : placeholder

    const tooltip = currentOption?.value
      ? `Webhook profile: ${currentOption.label}`
      : label

    const buttonText = currentOption?.value
      ? currentOption.label
      : placeholder

    return (buttonProps: ButtonShape) =>
      <DropdownButton
        {...buttonProps}
        isDisabled={isDisabled}
        label={label}
        title={tooltip}
      >
        {buttonText}
      </DropdownButton>
  }

  const onSuccessHandler = (webHooks: GetWebhooksListResponse) => {
    const disabledWebhooks = webHooks.items.filter(webhook => !webhook.enabled)

    const disabledWebhookIds = disabledWebhooks.map(webhook => webhook.uuid)

    onDisabledWebhooksChange(disabledWebhookIds)
  }

  return (
    <AuthenticatedDataRequest
      method='get'
      onSuccess={onSuccessHandler}
      shortErrorMessage='Failed to load webhook profile'
      url={NOTIFICATION_SERVICE + '/webhooks'}
    >
      {
        (data: GetWebhooksListResponse) =>
          <Combobox
            noOptionsPlaceholder={noDataPlaceholder}
            onChange={onChange}
            options={getOptions(data, selectedWebhookProfile)}
            renderButton={renderButton(getOptions(data, selectedWebhookProfile))}
            value={value}
          />
      }
    </AuthenticatedDataRequest>
  )
}

export default SelectWebhookProfile
